import { useCallback, useEffect, useRef, useState } from "react";
import { waitForPaymentAccess } from "./useGuidePurchase";
import { consumePendingGuidePurchase } from "../storage/pendingGuidePurchase";
import {
  clearStripeReturnFromUrl,
  readStripeReturn,
} from "../utils/stripeReturnUrl";

// VIVA-1214 — el comprador vuelve de la autenticación 3DS de su banco.
//
// `GuidePaymentModal` confirma con `redirect: "if_required"`: casi siempre el
// pago se resuelve sin salir de la página y lo atiende `GuidePurchasePanel`.
// Cuando el banco exige el reto, en cambio, el navegador se va y regresa al
// `return_url` —la liga de contratación— con una CARGA COMPLETA de la página:
// no hay panel abierto, no hay estado de React y nadie estaba esperando el
// resultado. Hasta el 25/09/2026 ese regreso no se leía y la pantalla se veía
// como si no hubiera pasado nada, con el pago ya hecho.
//
// LO QUE DECIDE EL RESULTADO ES EL SERVIDOR, no la URL (CA-09). `redirect_status`
// llega como parámetro y cualquiera puede escribirlo; aquí solo evita salir a
// preguntar por un pago que el propio Stripe ya reportó como fallido. Habilitar
// la guía sigue siendo trabajo del webhook, y esto únicamente espera a que haya
// corrido —preguntando al intento y, si se sabe cuál era, a mis-guias-compradas.

export const GUIDE_3DS_OUTCOME = {
  GRANTED: "granted",
  PENDING: "pending",
  FAILED: "failed",
};

export const useGuide3dsReturn = ({ enabled = false, lang, onGranted } = {}) => {
  const [checking, setChecking] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const activeRef = useRef(true);
  // El efecto no debe volver a arrancar si el componente se repinta: la URL ya
  // se limpió y el rastro en sessionStorage ya se consumió.
  const atendidoRef = useRef(false);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  const resolver = useCallback(
    async ({ paymentIntentId, guideId, redirectStatus }) => {
      if (redirectStatus === "failed") {
        setOutcome(GUIDE_3DS_OUTCOME.FAILED);
        return;
      }

      setChecking(true);
      const hasAccess = await waitForPaymentAccess({
        paymentIntentId,
        guideId,
        lang,
        isActive: () => activeRef.current,
      });
      if (!activeRef.current) return;

      setChecking(false);
      setOutcome(hasAccess ? GUIDE_3DS_OUTCOME.GRANTED : GUIDE_3DS_OUTCOME.PENDING);
      // La tarjeta cambia a "Leer versión completa" cuando el catálogo vuelve a
      // preguntar por las compras.
      if (hasAccess) onGranted?.({ paymentIntentId, guideId });
    },
    [lang, onGranted]
  );

  useEffect(() => {
    if (!enabled || atendidoRef.current) return;

    const retorno = readStripeReturn();
    if (!retorno) return;

    atendidoRef.current = true;
    // Antes de resolver, para que una recarga a medio camino no vuelva a
    // empezar y para no dejar el identificador del pago en el historial.
    clearStripeReturnFromUrl();

    const pendiente = consumePendingGuidePurchase();
    // El rastro solo vale si es de ESTE pago: si no coincide, es de otra compra
    // de la misma pestaña y su guía no tiene nada que ver con esta.
    const guideId =
      pendiente && String(pendiente.paymentIntentId) === String(retorno.paymentIntentId)
        ? pendiente.guideId
        : undefined;

    resolver({ ...retorno, guideId });
  }, [enabled, resolver]);

  return { checking, outcome };
};
